import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

/**
 * Wipe client-side state and start over.
 *
 * The recovery of last resort. Stale or malformed localStorage survives a page
 * reload, a browser restart, and a machine reboot — so a user stuck in this
 * state has no way out on their own, which is how a clinician lost an hour on
 * 2026-08-18. The auth token goes with it, so they land back on the login page.
 */
const clearLocalDataAndReload = () => {
  try { localStorage.clear(); } catch { /* privacy mode — nothing to clear */ }
  try { sessionStorage.clear(); } catch { /* same */ }
  window.location.href = '/';
};

const ErrorFallback = ({ error, resetError }) => (
  <div className="min-h-[60vh] flex items-center justify-center p-6">
    <div className="max-w-md w-full text-center">
      <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
        <ExclamationTriangleIcon className="w-8 h-8 text-red-600 dark:text-red-400" />
      </div>
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
        Something went wrong
      </h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
        An unexpected error occurred. You can try refreshing the page or going back to the dashboard.
      </p>
      <div className="flex items-center justify-center gap-3">
        <button
          type="button"
          onClick={() => window.location.href = '/admin'}
          className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          Go to Dashboard
        </button>
        <button
          type="button"
          onClick={resetError || (() => window.location.reload())}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
        >
          Try Again
        </button>
      </div>
      <button
        type="button"
        onClick={clearLocalDataAndReload}
        className="mt-4 text-xs text-gray-500 dark:text-gray-400 underline hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
      >
        Still stuck? Reset this browser and sign in again
      </button>
      {error && (
        <details className="mt-6 text-left">
          <summary className="text-xs text-gray-400 dark:text-gray-500 cursor-pointer hover:text-gray-600 dark:hover:text-gray-400">
            Technical details
          </summary>
          <pre className="mt-2 p-3 text-xs bg-gray-50 dark:bg-gray-900 rounded-lg overflow-auto text-gray-600 dark:text-gray-400 max-h-32">
            {error.message || String(error)}
          </pre>
        </details>
      )}
    </div>
  </div>
);

export default ErrorFallback;
